import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { CrossdPositionDetail } from "../types";
import { CROSS_DEFI_API } from "../constants";

export const CROSSD_POSITION_DETAIL_QUERY_KEY = "wp:crossd-position-details";

/** detail 응답의 envelope 유무 방어 파싱. */
function parseDetail(json: unknown): CrossdPositionDetail | null {
  const body = (json ?? {}) as { data?: CrossdPositionDetail } &
    CrossdPositionDetail;
  const detail = body.data ?? body;
  return detail?.token_id || detail?.pool ? detail : null;
}

/**
 * 포지션별 detail(`GET /api/v1/positions/{token_id}`)을 병렬 조회해
 * token_id → detail 맵을 만든다. list API에는 없는 pool.token0/token1의
 * 주소·decimals(WCROSS 판별 + 수량 표기)가 목적.
 */
export function useCrossdPositionDetails(
  env: Environment,
  tokenIds: string[],
  enabled = true,
) {
  const results = useQueries({
    queries: tokenIds.map((tokenId) => ({
      queryKey: [CROSSD_POSITION_DETAIL_QUERY_KEY, env, tokenId],
      queryFn: async () => {
        const res = await fetch(
          `${CROSS_DEFI_API[env]}/api/v1/positions/${tokenId}`,
          { cache: "no-store" },
        );
        if (!res.ok) {
          throw new Error(`crossd position detail failed: ${res.status}`);
        }
        return parseDetail(await res.json());
      },
      enabled: enabled && !!tokenId,
      staleTime: 60_000,
    })),
  });

  const detailMap = useMemo(() => {
    const map = new Map<string, CrossdPositionDetail>();
    results.forEach((r, i) => {
      const data = r.data;
      const tokenId = tokenIds[i];
      if (data && tokenId) map.set(tokenId, data);
    });
    return map;
  }, [results, tokenIds]);

  return { detailMap };
}
